import { useEffect, useRef, useState } from "react";

import { Message } from "@twilio/conversations";
import { LuLoader } from "react-icons/lu";

import useFirebaseContext from "../hooks/useFirebaseAuth";
import { UserRole } from "../types";

import ChatBubble from "./elements/ChatBubble";

const messageCache = new Map<string, Message[]>();
const errorCache = new Map<string, string | null>();

interface Props {
  lessonId: string;
  isVisible: boolean;
}

function ReadOnlyChatPane({ lessonId, isVisible }: Props) {
  const { authenticatedAxios, userRole } = useFirebaseContext();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const hasInitialized = useRef(false);

  const isOwnMessage = (messageAuthor: string): boolean => {
    const currentUserRole = userRole;

    if (
      currentUserRole === UserRole.Tutor &&
      messageAuthor.startsWith("Tutor -")
    ) {
      return true;
    } else if (
      currentUserRole === UserRole.Student &&
      !messageAuthor.startsWith("Tutor -")
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!lessonId || hasInitialized.current) return;

    if (messageCache.has(lessonId)) {
      setMessages(messageCache.get(lessonId)!);
      setError(errorCache.get(lessonId) || null);
      setIsLoading(false);
      hasInitialized.current = true;
      return;
    }

    const fetchMessages = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await authenticatedAxios.get(
          `/lessons/conversation/${lessonId}`
        );
        const fetchedMessages = response.data.messages;

        // Cache the successful response
        messageCache.set(lessonId, fetchedMessages);
        errorCache.set(lessonId, null);

        setMessages(fetchedMessages);
        setIsLoading(false);
        setError(null);
      } catch (err) {
        console.error("Error fetching chat history:", err);
        const errorMessage = "Failed to load chat history";

        // Cache the error state
        errorCache.set(lessonId, errorMessage);

        setError(errorMessage);
        setIsLoading(false);
      } finally {
        hasInitialized.current = true;
      }
    };

    fetchMessages();
  }, [lessonId, authenticatedAxios]);

  useEffect(() => {
    if (messageListRef.current && !isLoading && isVisible) {
      const timer = setTimeout(() => {
        if (messageListRef.current) {
          messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages, isLoading, isVisible]);

  // Function to clear cache (useful for manual refresh)
  const clearCache = () => {
    messageCache.delete(lessonId);
    errorCache.delete(lessonId);
    hasInitialized.current = false;
  };

  // Function to refresh messages
  const refreshMessages = async () => {
    clearCache();
    hasInitialized.current = false;

    try {
      setIsLoading(true);
      setError(null);

      const response = await authenticatedAxios.get(
        `/lessons/conversation/${lessonId}`
      );
      const fetchedMessages = response.data.messages;

      messageCache.set(lessonId, fetchedMessages);
      errorCache.set(lessonId, null);

      setMessages(fetchedMessages);
      setIsLoading(false);
      setError(null);
    } catch (err) {
      console.error("Error refreshing chat history:", err);
      const errorMessage = "Failed to load chat history";

      errorCache.set(lessonId, errorMessage);

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="chat-pane flex flex-col h-full pb-3">
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center gap-2 text-gray-500">
            <LuLoader className="w-4 h-4 animate-spin" />
            <span>Loading chat history...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-pane flex flex-col h-full pb-3">
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-red-500">
            <p className="text-sm">{error}</p>
            <button
              onClick={refreshMessages}
              className="mt-2 text-blue-500 hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`chat-pane flex flex-col h-full pb-3 ${!isVisible ? "hidden" : "block"
        }`}
    >
      <div
        ref={messageListRef}
        className="message-list h-full overflow-y-auto flex flex-col gap-2 py-2 px-3"
      >
        {!messages || messages.length === 0 && !isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <p className="text-sm">No messages found</p>
            </div>
          </div>
        ) : (
          messages?.map((message) => (
            <ChatBubble
              key={message.sid + message.index}
              message={message as unknown as Message}
              isOwnMessage={isOwnMessage(message.author || "")}
            />
          ))
        )}
      </div>
      <div className="message-input-container flex items-center justify-center space-x-2 pt-3 px-3 border-t border-slate-200">
        <div className="text-sm text-gray-500 italic">Read-only mode</div>
      </div>
    </div>
  );
}

export default ReadOnlyChatPane;
